/**
 * @file PartialRefundInvoice.js is responsible for rendering the partial refund invoice modal
 */
import React, { useState } from "react";

import {
	Button,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Text,
	Input,
	FormControl,
	FormLabel,
	Select,
	VStack,
	Box,
	Divider,
} from "@chakra-ui/react";

import ErrorMsg from "Blueprints/ErrorMsg";

import useCreatePartialRefund from "hooks/Invoice/mutations/useCreatePartialRefund";
import { displayDollarAmount } from "utils/utils";

/**
 * @function PartialRefundInvoice
 *
 * @summary - Modal to create a partial refund for an invoice
 */
const PartialRefundInvoice = ({ isOpen, onClose, invoice, refetch }) => {
	const [loading, setLoading] = useState(false);
	const [errorMsg, setErrorMsg] = useState(null);
	const [input, setInput] = useState({
		amount: "",
		reason: "requested_by_customer",
	});

	const { createPartialRefund } = useCreatePartialRefund();

	// Define valid refund reasons for Stripe
	const refundReasons = [
		{ value: "requested_by_customer", label: "Requested by Customer" },
		{ value: "duplicate", label: "Duplicate Payment" },
		{ value: "fraudulent", label: "Fraudulent Transaction" },
	];

	const totalAmountWithFees =
		invoice.total_amount + (invoice.tax_fee ?? 0) + (invoice.transaction_fee ?? 0);

	// Calculate refund info
	const totalRefunded =
    invoice.refunds?.reduce((sum, refund) => sum + refund.amount, 0) || 0;
	const remainingAmount = totalAmountWithFees - totalRefunded;
	const maxRefundAmount = remainingAmount;

	console.log("PartialRefundInvoice", {
		invoice,
		totalAmountWithFees,
		totalRefunded,
		remainingAmount,
		maxRefundAmount,
	});

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setInput((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const validateInput = () => {
		if (!input.amount || parseFloat(input.amount) <= 0) {
			setErrorMsg("Please enter a valid refund amount.");
			return false;
		}
		if (parseFloat(input.amount) > maxRefundAmount) {
			setErrorMsg(
				`Refund amount cannot exceed ${displayDollarAmount(maxRefundAmount)}.`
			);
			return false;
		}
		setErrorMsg(null);
		return true;
	};

	/**
   * @function handleCreatePartialRefund
   *
   * @description - This function is used to create a partial refund for an invoice.
   */
	const handleCreatePartialRefund = async (e) => {
		e.stopPropagation();
		e.preventDefault();

		if (!validateInput()) return;

		setLoading(true);

		let response = await createPartialRefund(invoice._id, {
			amount: parseFloat(input.amount),
			reason: input.reason,
		});

		if (response.error) {
			setErrorMsg(response.error.message);
		} else {
			await refetch();
			onClose();
			resetForm();
		}
		setLoading(false);
	};

	const resetForm = () => {
		setInput({ amount: "", reason: "requested_by_customer" });
		setErrorMsg(null);
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="md">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Partial Refund Invoice</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<VStack spacing={4} align="stretch">
						<Box>
							<Text fontSize="sm" color="gray.600">
                Invoice Total (with Tax and Fees):
							</Text>
							<Text fontWeight="bold">
								{displayDollarAmount(totalAmountWithFees)}
							</Text>
						</Box>

						<Box>
							<Text fontSize="sm" color="gray.600">
                Total Refunded:
							</Text>
							<Text fontWeight="bold">
								{displayDollarAmount(totalRefunded)}
							</Text>
						</Box>

						<Box>
							<Text fontSize="sm" color="gray.600">
                Remaining Balance:
							</Text>
							<Text fontWeight="bold" color="green.600">
								{displayDollarAmount(remainingAmount)}
							</Text>
						</Box>

						<Divider />

						{errorMsg && <ErrorMsg error={errorMsg} />}

						<FormControl isRequired>
							<FormLabel>Refund Amount</FormLabel>
							<Input
								type="number"
								name="amount"
								value={input.amount}
								onChange={handleInputChange}
								placeholder="0.00"
								min="0.01"
								max={maxRefundAmount}
								step="0.01"
							/>
						</FormControl>

						<FormControl isRequired>
							<FormLabel>Reason for Refund</FormLabel>
							<Select
								name="reason"
								value={input.reason}
								onChange={handleInputChange}
							>
								{refundReasons.map((reason) => (
									<option key={reason.value} value={reason.value}>
										{reason.label}
									</option>
								))}
							</Select>
						</FormControl>
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button
						variant="primary"
						mr={3}
						isLoading={loading}
						onClick={handleCreatePartialRefund}
						isDisabled={!input.amount || parseFloat(input.amount) <= 0}
					>
            Process Partial Refund
					</Button>
					<Button variant="ghost" onClick={handleClose}>
            Cancel
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default PartialRefundInvoice;
